import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO } from "../lib/config";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = NAV_LINKS.filter((l) => l.label !== "Donate");

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-sm"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-24 md:h-28">
        {/* Logo lockup */}
        <Link to="/" className="focus-ring group flex items-center" data-testid="logo-link" aria-label="Jeffrey Trickler home">
          <img
            src={LOGO}
            alt="Jeff Trickler for Ontario County Treasurer"
            className="h-20 md:h-24 w-auto rounded-sm shadow-sm transition-transform group-hover:scale-[1.03]"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-base font-medium tracking-wide transition-colors focus-ring ${
                  isActive ? "text-red-700" : "text-slate-700 hover:text-[hsl(var(--navy))]"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {link.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-2 left-0 right-0 h-[2px] bg-red-700"
                    />
                  )}
                </span>
              )}
            </NavLink>
          ))}
          <Link
            to="/donate"
            data-testid="nav-donate-button"
            className="focus-ring bg-red-700 text-white text-base font-semibold px-7 py-3 rounded-sm hover:bg-red-800 transition-colors"
          >
            Donate
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          className="lg:hidden focus-ring p-2 text-[hsl(var(--navy))]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-200"
            data-testid="mobile-nav"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 text-base font-medium border-b border-slate-100 ${
                      isActive ? "text-red-700" : "text-slate-800"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/donate"
                data-testid="mobile-nav-donate-button"
                className="mt-4 bg-red-700 text-white text-center font-semibold px-6 py-3 rounded-sm"
              >
                Donate
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
